import { Box, Card, CardContent } from "@mui/material";
import CaseIntakesAutocomplete from "components/CaseIntakesAutocomplete";
import IntakeViewer from "components/DocumentViewer/IntakeViewer";
import FormFieldPopover from "components/FormField/FormFieldPopover";
import { FormFieldSelect } from "components/FormFieldSelect";
import { Case, DocumentIntakeBookmark, Form } from "generated/graphql";
import { Suspense, useMemo, useState } from "react";
import HitsOnPage from "./HitsOnPage";
import { Await, useNavigate } from "react-router-dom";
import { useLoaderData } from "react-router-typesafe";
import { useAppSelector } from "redux/hooks";
import { useParamNumbers } from "helpers/hooks/useParamNumbers";
import { selectIntakes } from "redux/slices/case";
import { selectBookmarks } from "redux/slices/intakeViewer";
import CollapsibleSections, { VirtulizedCollapsibleSections } from "components/CollapsibleSections";
import theme from "theme";
import useSearchParamsWithUpdate from "helpers/hooks/useSearchParamsWithUpdate";
import { documentReviewLoader } from "../routes/review.loader";
import { cn } from "components/lib/utils";

interface IntakesReviewProps {
  lensId: number;
  caseId: number;
  form: Form;
  mycase: Case;
}

function Bookmarks({
  bookmarks,
  onClick,
  page,
}: {
  bookmarks: DocumentIntakeBookmark[];
  page: number;
  onClick: (pageNumber: number) => void;
}) {
  const bookmarksByPage: {
    [pageNumber: number]: {
      title: string;
      bookmarks: DocumentIntakeBookmark[];
    };
  } = useMemo(() => {
    return bookmarks.reduce((bookmarksByPage, bookmark) => {
      const pageIndexFound = bookmarksByPage[bookmark.intakePage];

      if (pageIndexFound !== undefined) {
        bookmarksByPage[bookmark.intakePage]?.bookmarks.push(bookmark);
      } else {
        bookmarksByPage[bookmark.intakePage] = {
          title: bookmark.title,
          bookmarks: [],
        };
      }

      return bookmarksByPage;
    }, {});
  }, [bookmarks]);

  const bookmarkSections = useMemo(
    () =>
      Object.entries(bookmarksByPage).map((b) => ({
        title: b[1].title,
        pageNumber: parseInt(b[0]),
        bookmarks: b[1].bookmarks,
      })),
    [bookmarksByPage]
  );

  const renderBookmarkSections = useMemo(
    () =>
      bookmarkSections.map((bs) => {
        const selected = bs.pageNumber === page;
        return {
          title: (
            <p className={cn("text-xs", { "font-semibold": selected })} title={`${bs.title} - ${bs.pageNumber}`}>
              Page {bs.pageNumber}
            </p>
          ),
          expanded: true,
          items: [{ title: bs.title }, ...bs.bookmarks].map((b, idx) => (
            <p
              className="text-xxs pl-2 text-ellipsis overflow-hidden whitespace-nowrap m-0"
              key={b.title + "_" + idx}
              title={b.title}>
              {b.title}
            </p>
          )),
          selected,
          containerSX: {
            backgroundColor: selected ? theme.palette.grey[50] : undefined,
            borderRadius: `${theme.shape.borderRadius}px`,
            px: 1,
            py: 0.5,
            cursor: "pointer",
            ":hover": {
              backgroundColor: theme.palette.grey[50],
              " .MuiTypography-root.MuiTypography-xs": {
                fontWeight: 600,
              },
            },
          },
          onClick: () => onClick(bs.pageNumber),
        };
      }),
    [bookmarkSections, page]
  );

  return bookmarkSections.length > 0 ? (
    <Box className="mb-2 flex flex-col max-h-[500px]">
      <p className="text-xs font-semibold mb-2 text-gray-700">Bookmarked Pages</p>
      {renderBookmarkSections.length < 20 ? (
        <div className="max-h-[400px] overflow-y-auto">
          <CollapsibleSections sections={renderBookmarkSections} />
        </div>
      ) : (
        <VirtulizedCollapsibleSections height={400} sections={renderBookmarkSections} />
      )}
    </Box>
  ) : (
    <></>
  );
}

const IntakesReview = ({ lensId, caseId, form }: IntakesReviewProps) => {
  const { searchParams, updateParam, updateParams } = useSearchParamsWithUpdate();
  const paramNumbers = useParamNumbers(["documentId"]);
  const intakeId = paramNumbers.documentId;
  const qpPage = searchParams.get("page");
  const navigate = useNavigate();
  const page = qpPage ? parseInt(qpPage, 10) : 1;
  const qpProfileFieldId = searchParams.get("profileFieldId");
  const profileFieldId = qpProfileFieldId ? parseInt(qpProfileFieldId, 10) : undefined;
  const [blockIndex, setBlockIndex] = useState<number | undefined | null>();
  const navigateToBlock = (intakeId: number, page: number, index?: number | null) => {
    updateParams([
      { key: "documentId", value: intakeId.toString() },
      { key: "page", value: page.toString() },
    ]);
    setBlockIndex(index);
  };
  const intakes = useAppSelector(selectIntakes);
  const intake = intakeId ? intakes.find((i) => i.id === intakeId) : undefined;
  const bookmarks = useAppSelector(selectBookmarks);
  const navigateToPage = (pageNumber: number) => {
    updateParam("page", pageNumber.toString());
  };
  const deferredData = useLoaderData<typeof documentReviewLoader>();
  return (
    <div className="grid grid-cols-[400px_1fr] gap-x-4 h-full bg-gray-50 min-h-0 mx-2">
      <div>
        <Card className="mb-4">
          <CardContent>
            <p className="text-xs font-semibold mb-2 text-gray-700">Select Document</p>
            <div className="mb-4">
              <CaseIntakesAutocomplete
                caseId={caseId}
                selected={intake}
                onChange={(intake) => {
                  navigate(`/lens/${lensId}/cases/${caseId}/documents/${intake?.id}?page=1`);
                }}
              />
            </div>
            <Suspense fallback={<></>}>
              <Await resolve={deferredData?.deferredBookmarks}>
                {() => <Bookmarks bookmarks={bookmarks} onClick={navigateToPage} page={page} />}
              </Await>
            </Suspense>
            <p className="text-xs font-semibold mb-2 text-gray-700">Select Profile Field</p>
            <div className="flex items-center">
              <FormFieldSelect
                fieldId={profileFieldId}
                changeFieldId={(fieldId) => updateParam("profileFieldId", fieldId?.toString())}
                sx={{ flexGrow: 1 }}
                allowNone={true}
                noneLabel="All"
              />
              {form !== undefined && profileFieldId !== undefined && (
                <FormFieldPopover
                  form={form}
                  fieldId={profileFieldId}
                  navigateToBlock={navigateToBlock}
                  hideLightbulb={true}
                />
              )}
            </div>
          </CardContent>
        </Card>
        {intake && (
          <HitsOnPage intake={intake} pageNum={page} profileFieldId={profileFieldId} formFields={form.fields} />
        )}
      </div>
      {intake ? (
        <IntakeViewer
          lensId={lensId}
          caseId={caseId}
          pageNumberHandle={[page, (newPage) => updateParam("page", newPage.toString())]}
          additionalAnnotations={[]}
          parentBlockIndex={blockIndex}
          intake={intake}
          defaultShowHighlightLensHits
          lensHitFilterId={profileFieldId}
          pinForm={{
            mode: "panel",
            cardFormFragmentProps: {
              compact: true,
            },
          }}
        />
      ) : (
        <div className="flex place-items-center mt-4 h-full">
          <div className="text-center">
            <p className="text-4xl font-extrabold">No document selected</p>
            <p className="text-2xl font-extrabold text-gray-500">Select a document to begin</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default IntakesReview;
